import { useEffect, useState, type ReactNode } from 'react';
import { wsClient, type ConnectionState } from '../ws';
import { useApp } from '../store';
import { Spinner } from './ui';
import { useNewVersionAvailable } from '../appVersion';

// Thin banner(s) pinned to the top of the app while a realtime connection is
// not healthy. There are two INDEPENDENT connections:
//   1. The WS gateway (text/presence/typing) — reconnect engine in ws.ts.
//   2. The LiveKit room (voice/video) — runs on a separate host, reconnects
//      internally; its state is mirrored into the store by
//      VoiceConnectionWatcher (in VoiceConnection.tsx).
// Either can drop without the other, so we render a row per active problem and
// stack them. Both reconnect automatically — the banner is just feedback so the
// user knows not to refresh.
//
// We debounce the WS banner: a brief blip or the normal initial connect (which
// flips connecting→connected in well under a second) shouldn't flash a scary
// bar. Only a stall longer than SHOW_DELAY_MS reveals it.
const SHOW_DELAY_MS = 1200;

export function ConnectionBanner() {
  const [wsState, setWsState] = useState<ConnectionState>(() => wsClient.getState());
  const [wsVisible, setWsVisible] = useState(false);
  const voiceReconnecting = useApp((s) => s.voiceReconnecting);
  // Offer the reload only outside a call — reloading drops you from voice.
  const newVersion = useNewVersionAvailable();
  const inCall = useApp((s) => s.activeVoice !== null);
  const [updateDismissed, setUpdateDismissed] = useState(false);
  const showUpdate = newVersion && !inCall && !updateDismissed;

  // Track WS connection state.
  useEffect(() => wsClient.onState(setWsState), []);

  // Debounced WS visibility: show only after we've been non-connected for a
  // beat; hide immediately on reconnect.
  useEffect(() => {
    if (wsState === 'connected') {
      setWsVisible(false);
      return;
    }
    const t = window.setTimeout(() => setWsVisible(true), SHOW_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [wsState]);

  if (!wsVisible && !voiceReconnecting && !showUpdate) return null;

  return (
    <div className="fixed top-0 inset-x-0 z-60 flex flex-col animate-slide-down">
      {wsVisible && <Bar label="Connection lost — reconnecting…" />}
      {voiceReconnecting && <Bar label="Voice connection dropped — reconnecting…" />}
      {showUpdate && !wsVisible && (
        <Bar label="A new version of the app is available" spinner={false}>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="ml-1 px-2 py-0.5 rounded-sm bg-accent-text/15 hover:bg-accent-text/25 font-semibold"
          >
            Reload
          </button>
          <button
            type="button"
            onClick={() => setUpdateDismissed(true)}
            className="px-2 py-0.5 rounded-sm hover:bg-accent-text/15"
          >
            Later
          </button>
        </Bar>
      )}
    </div>
  );
}

function Bar({
  label,
  spinner = true,
  children,
}: {
  label: string;
  spinner?: boolean;
  children?: ReactNode;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex items-center justify-center gap-2 px-3 py-1.5
                 text-xs font-medium text-accent-text
                 bg-linear-to-b from-accent to-accent-hover
                 border-b border-border shadow-md"
    >
      {spinner && <Spinner size={12} />}
      <span>{label}</span>
      {children}
    </div>
  );
}
